import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import LanguageSwitcher from '../components/LanguageSwitcher';

// Canonical industry IDs sent to the API. Display labels are looked up
// from the i18n bundle at render time (`onboarding.sme.step1.industries.*`)
// so adding a new locale doesn't require a code change. The IDs themselves
// MUST stay stable — they're the values persisted on the tenant.
const INDUSTRIES = [
  'softwareSaas',
  'healthcare',
  'financeBanking',
  'ecommerceRetail',
  'education',
  'manufacturing',
  'professionalServices',
  'other',
] as const;

// Canonical industry label sent to the API. Maps the i18n ID above to the
// stable English string the backend has always seen so the API contract
// doesn't change. (Keep in sync with the previous hardcoded list.)
const INDUSTRY_API_LABEL: Record<(typeof INDUSTRIES)[number], string> = {
  softwareSaas: 'Software / SaaS',
  healthcare: 'Healthcare',
  financeBanking: 'Finance & Banking',
  ecommerceRetail: 'E-commerce & Retail',
  education: 'Education',
  manufacturing: 'Manufacturing',
  professionalServices: 'Professional Services',
  other: 'Other',
};

// Regulatory option IDs — also stable strings sent to the API. These
// happen to match their display labels in English (HIPAA, GDPR, ...) but
// the lookup goes through i18n anyway so a translated locale can render
// "Règlement HIPAA" or similar without changing the persisted value.
const REGULATORY_OPTIONS = [
  'HIPAA',
  'GDPR',
  'DISHA',
  'PCI-DSS',
  'SOC 2',
  'ISO 27001',
  'None',
] as const;

const ROLES = ['PRODUCT_MANAGER', 'DEVELOPER', 'QA_ENGINEER', 'ARCHITECT'] as const;

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: 44,
  background: 'var(--surface-alt)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '0 14px',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box' as const,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 4,
};

interface Invite {
  email: string;
  role: string;
}

export default function SMEOnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Step 1 — Organisation Profile
  const [orgName, setOrgName] = useState('');
  // Industry state holds the canonical ID (e.g., 'softwareSaas'); the
  // PATCH body translates back to the API label below.
  const [industry, setIndustry] = useState<string>('');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );

  // Step 2 — Team Setup
  const [invites, setInvites] = useState<Invite[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>(ROLES[0]);

  // Step 3 — Regulatory Context
  const [regulatoryContext, setRegulatoryContext] = useState<string[]>([]);

  // Step 4 — Delivery Mode
  const [deliveryMode, setDeliveryMode] = useState<'managed' | 'byoc'>('managed');

  const timezones = (() => {
    try {
      return Intl.supportedValuesOf('timeZone');
    } catch {
      return [timezone];
    }
  })();

  const handleAddInvite = () => {
    const trimmed = inviteEmail.trim();
    if (!trimmed) return;
    if (invites.length >= 5) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;
    if (invites.some(i => i.email.toLowerCase() === trimmed.toLowerCase())) return;
    setInvites([...invites, { email: trimmed, role: inviteRole }]);
    setInviteEmail('');
  };

  const handleRemoveInvite = (email: string) => {
    setInvites(invites.filter(i => i.email !== email));
  };

  const toggleRegulatory = (item: string) => {
    if (item === 'None') {
      setRegulatoryContext(prev => prev.includes('None') ? [] : ['None']);
      return;
    }
    setRegulatoryContext(prev => {
      const filtered = prev.filter(r => r !== 'None');
      return filtered.includes(item)
        ? filtered.filter(r => r !== item)
        : [...filtered, item];
    });
  };

  const handleComplete = async () => {
    setSubmitting(true);
    setError('');
    try {
      await apiFetch('/tenants/me/onboarding', {
        method: 'PATCH',
        body: JSON.stringify({
          orgName: orgName.trim() || undefined,
          // Send the stable English label, not the i18n key — the API
          // contract has always taken the human label.
          industry: industry
            ? INDUSTRY_API_LABEL[industry as (typeof INDUSTRIES)[number]]
            : undefined,
          timezone,
          regulatoryContext: regulatoryContext.filter(r => r !== 'None'),
          deliveryMode,
          invites: invites.length > 0 ? invites : undefined,
        }),
      });
      // Navigate to /projects, not /dashboard — the SME defaults summary card
      // (ACC.SME.A3 / PR #489) is rendered by ProjectsPage. Sending the user to
      // /dashboard (Dashboard) would skip past the card entirely.
      navigate('/projects', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('onboarding.sme.errors.onboardingFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { num: 1, label: t('onboarding.sme.stepLabels.organisationProfile') },
    { num: 2, label: t('onboarding.sme.stepLabels.teamSetup') },
    { num: 3, label: t('onboarding.sme.stepLabels.regulatoryContext') },
    { num: 4, label: t('onboarding.sme.stepLabels.deliveryMode') },
  ];

  const hasRegInfo = regulatoryContext.some(r => ['HIPAA', 'GDPR', 'DISHA'].includes(r));

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--bg)' }}>
      {/* Sidebar */}
      <div style={{ width: 260, background: 'var(--surface)', borderRight: '1px solid var(--border)', padding: '32px 20px', flexShrink: 0 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 24 }}>
          {t('common.appName')}
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {steps.map(s => {
            const isComplete = s.num < step;
            const isCurrent = s.num === step;
            return (
              <div key={s.num} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-display)',
                  background: isComplete ? 'var(--success)' : isCurrent ? 'var(--primary)' : 'var(--surface-alt)',
                  color: isComplete || isCurrent ? '#fff' : 'var(--text-secondary)',
                  border: isCurrent ? 'none' : '1px solid var(--border)',
                }}>
                  {isComplete ? '\u2713' : s.num}
                </div>
                <span style={{
                  fontFamily: 'var(--font-body)', fontSize: 13,
                  fontWeight: isCurrent ? 600 : 400,
                  color: isCurrent ? 'var(--text)' : 'var(--text-secondary)',
                }}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '40px 48px', maxWidth: 640 }}>
        {error && (
          <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 20, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)' }}>
            {error}
          </div>
        )}

        {/* Step 1 — Organisation Profile */}
        {step === 1 && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              {t('onboarding.sme.step1.title')}
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 32 }}>
              {t('onboarding.sme.step1.subtitle')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>{t('onboarding.sme.step1.orgNameLabel')}</label>
                <input
                  type="text"
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  placeholder={t('onboarding.sme.step1.orgNamePlaceholder')}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>{t('onboarding.sme.step1.industryLabel')}</label>
                <select
                  value={industry}
                  onChange={e => setIndustry(e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="">{t('onboarding.sme.step1.industryPlaceholder')}</option>
                  {INDUSTRIES.map(ind => (
                    <option key={ind} value={ind}>
                      {t(`onboarding.sme.step1.industries.${ind}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>{t('onboarding.sme.step1.timezoneLabel')}</label>
                <select
                  value={timezone}
                  onChange={e => setTimezone(e.target.value)}
                  data-testid="timezone-select"
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  {timezones.map(tz => (
                    <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {/* Step 2 — Team Setup */}
        {step === 2 && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              {t('onboarding.sme.step2.title')}
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 32 }}>
              {t('onboarding.sme.step2.subtitle')}
            </p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder={t('onboarding.sme.step2.emailPlaceholder')}
                style={{ ...inputStyle, flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddInvite(); } }}
              />
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
                style={{ ...inputStyle, width: 160 }}
              >
                {ROLES.map(r => (
                  <option key={r} value={r}>{t(`onboarding.sme.step2.roles.${r}`)}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddInvite}
                disabled={!inviteEmail.trim() || invites.length >= 5}
                style={{
                  height: 44,
                  padding: '0 16px',
                  background: 'var(--primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: !inviteEmail.trim() || invites.length >= 5 ? 'not-allowed' : 'pointer',
                  opacity: !inviteEmail.trim() || invites.length >= 5 ? 0.5 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {t('onboarding.sme.step2.addButton')}
              </button>
            </div>

            {invites.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {invites.map(inv => (
                  <div
                    key={inv.email}
                    data-testid={`invite-${inv.email}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    <div>
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>
                        {inv.email}
                      </span>
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginLeft: 8 }}>
                        {t(`onboarding.sme.step2.roles.${inv.role}`)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveInvite(inv.email)}
                      aria-label={t('onboarding.sme.step2.removeAriaLabel', { email: inv.email })}
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: 'var(--error)', fontFamily: 'var(--font-body)', fontSize: 12,
                        fontWeight: 600, padding: '4px 8px',
                      }}
                    >
                      {t('onboarding.sme.step2.removeButton')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {invites.length === 0 && (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                {t('onboarding.sme.step2.emptyState')}
              </p>
            )}
          </>
        )}

        {/* Step 3 — Regulatory Context */}
        {step === 3 && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              {t('onboarding.sme.step3.title')}
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 32 }}>
              {t('onboarding.sme.step3.subtitle')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
              {REGULATORY_OPTIONS.map(opt => {
                const isSelected = regulatoryContext.includes(opt);
                return (
                  <div
                    key={opt}
                    data-testid={`reg-card-${opt}`}
                    onClick={() => toggleRegulatory(opt)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRegulatory(opt); } }}
                    style={{
                      padding: '16px 14px',
                      textAlign: 'center',
                      borderRadius: 'var(--radius-sm)',
                      border: isSelected ? '2px solid var(--primary)' : '1px solid var(--border)',
                      background: isSelected ? 'rgba(124,58,237,0.06)' : 'var(--surface)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-body)',
                      fontSize: 13,
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? 'var(--primary)' : 'var(--text)',
                      transition: 'all 150ms',
                    }}
                  >
                    {t(`onboarding.sme.step3.options.${opt}`)}
                  </div>
                );
              })}
            </div>

            {hasRegInfo && (
              <div style={{
                padding: '12px 16px',
                background: 'rgba(99,102,241,0.06)',
                border: '1px solid rgba(99,102,241,0.2)',
                borderRadius: 'var(--radius-sm)',
              }}>
                <p data-testid="reg-info-banner" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                  {t('onboarding.sme.step3.infoBanner')}
                </p>
              </div>
            )}
          </>
        )}

        {/* Step 4 — Delivery Mode */}
        {step === 4 && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              {t('onboarding.sme.step4.title')}
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 32 }}>
              {t('onboarding.sme.step4.subtitle')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {([
                {
                  value: 'managed' as const,
                  title: t('onboarding.sme.step4.managed.title'),
                  description: t('onboarding.sme.step4.managed.description'),
                },
                {
                  value: 'byoc' as const,
                  title: t('onboarding.sme.step4.byoc.title'),
                  description: t('onboarding.sme.step4.byoc.description'),
                },
              ]).map(option => {
                const isSelected = deliveryMode === option.value;
                return (
                  <div
                    key={option.value}
                    data-testid={`delivery-${option.value}`}
                    onClick={() => setDeliveryMode(option.value)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDeliveryMode(option.value); } }}
                    style={{
                      padding: '20px',
                      borderRadius: 'var(--radius-sm)',
                      border: isSelected ? '2px solid var(--primary)' : '1px solid var(--border)',
                      background: isSelected ? 'rgba(124,58,237,0.06)' : 'var(--surface)',
                      cursor: 'pointer',
                      transition: 'all 150ms',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                      <div style={{
                        width: 18, height: 18, borderRadius: '50%',
                        border: isSelected ? '5px solid var(--primary)' : '2px solid var(--border)',
                        background: 'var(--surface)',
                        flexShrink: 0,
                      }} />
                      <span style={{
                        fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600,
                        color: 'var(--text)',
                      }}>
                        {option.title}
                      </span>
                    </div>
                    <p style={{
                      fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)',
                      margin: 0, paddingLeft: 30, lineHeight: 1.5,
                    }}>
                      {option.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
          {step > 1 ? (
            <button
              onClick={() => setStep(step - 1)}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                padding: '10px 20px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', background: 'transparent',
                color: 'var(--text)', cursor: 'pointer',
              }}
            >
              {t('common.buttons.back')}
            </button>
          ) : <div />}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* LanguageSwitcher self-hides while only one locale ships, so
                this footer slot is invisible today and lights up the moment
                a second locale is added in i18n.ts. */}
            <LanguageSwitcher />

            {step === 2 && (
              <button
                onClick={() => setStep(step + 1)}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                  padding: '10px 20px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text)', cursor: 'pointer',
                }}
              >
                {t('common.buttons.skipForNow')}
              </button>
            )}

            {step < 4 ? (
              <button
                onClick={() => setStep(step + 1)}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                  padding: '10px 24px', borderRadius: 'var(--radius-sm)',
                  border: 'none', background: 'var(--primary)', color: '#fff',
                  cursor: 'pointer', height: 44,
                }}
              >
                {t('common.buttons.continue')}
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={submitting}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                  padding: '10px 24px', borderRadius: 'var(--radius-sm)',
                  border: 'none', background: 'var(--success)', color: '#fff',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1, height: 44,
                }}
              >
                {submitting
                  ? t('onboarding.sme.buttons.settingUp')
                  : t('onboarding.sme.buttons.completeSetup')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
